import React, { useState, useEffect } from "react";
import FormTextField from "components/form/form-textfield";
import FormChipInput from "components/form/form-chipinput";
import FormDatePicker from "components/form/form-datepicker";
import DataGrid, { DataGridItem } from "components/data-grid";
import withStyles from "components/with-styles";
import { GraphicSetSelectByContract, TemplateSelect, EventTypeSelect } from "components/select";
import styles from "./styles/basic-form.css.js";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";
import DurationHumanizer from "components/duration-humanizer";
import { useAuthDataContext, RestrictedToAdmin } from "components/auth";
import { eventStatusTypes } from "pages/event/utils";
import { PageSection, PageHeaderBar } from "components/page";
import { handleStartDateChange } from ".";
import FormCheckbox from "components/form/form-checkbox";
import { getRoomTemplateQuery } from "pages/room-template";
import { useApolloContext } from "components/graphql";
import { useLoadingContext } from "components/loading-spinner-overlay";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { connect } from "formik";
import Icon from "components/icon";
import Tooltip from "@material-ui/core/Tooltip";
import ActionWithModal from "../../../../components/buttons/action-button/action-with-modal";

const isStartEditionDisabled = values =>
  [eventStatusTypes.FINISHED, eventStatusTypes.STARTED, eventStatusTypes.STARTING].includes(values.eventStatus);

const isEndEditionDisabled = values => values.eventStatus === eventStatusTypes.FINISHED;

export const LOCAL_REC_EVENT = "LOCAL_REC_EVENT";

const BasicForm = ({
  isNewForm = false,
  intl,
  classes,
  values,
  isAuthorizedRecording,
  eventToEdit,
  handleChange,
  canEditEventType = false,
  canEditRoomTemplate = false,
  customer,
  errorTextMixerSlots,
  showLocalRecEventFields = false,
  showTestTemplates = false,
}) => {
  const client = useApolloContext();
  const { setLoading } = useLoadingContext();
  const [includeTestTemplates, setIncludeTestTemplates] = useState(showTestTemplates);
  const [localRecModalOpen, setLocalRecModalOpen] = useState(false);
  const { hasAdminPrivileges } = useAuthDataContext();
  const { currentCustomerId } = useCurrentCustomerContext();

  const canEditStart = isNewForm || !isStartEditionDisabled(values);
  const canEditEnd = isNewForm || !isEndEditionDisabled(values);

  const [isCheckedTestTemplates, setIsCheckedTestTemplates] = useState(false);

  const handleChangeIncludeTestTemplates = () => value => setIncludeTestTemplates(value);

  const handleChangeRoomTemplateId = e => handleChange("roomTemplateId")(e.target.value);

  useEffect(
    () => {
      if (isNewForm && values.roomTemplateId) {
        setLoading(true);
        client
          .query(
            getRoomTemplateQuery({
              roomTemplateId: values.roomTemplateId,
              customerId: currentCustomerId,
              contractId: values.contractId,
              hasAdminPrivileges,
            }),
          )
          .then(({ data, error }) => {
            if (error) {
              console.error(error);
            } else {
              const roomTemplate = data.roomTemplate;
              if (roomTemplate) {
                if (roomTemplate.type === LOCAL_REC_EVENT) {
                  handleChange("isLocalRecEvent")(true);
                } else {
                  handleChange("isLocalRecEvent")(false);
                }
                if (!roomTemplate.test) {
                  handleChange("isInternalTest")(false);
                  handleChange("visible")(true);
                  handleChange("billable")(true);
                } else {
                  handleChange("isInternalTest")(true);
                  handleChange("visible")(false);
                  handleChange("billable")(false);
                  if (!!roomTemplate.test) {
                    setIsCheckedTestTemplates(true);
                  }
                }
              } else {
                handleChange("isLocalRecEvent")(false);
              }
            }
          })
          .finally(() => setLoading(false));
      }
    },
    [
      client,
      currentCustomerId,
      handleChange,
      hasAdminPrivileges,
      isNewForm,
      setLoading,
      values.contractId,
      values.roomTemplateId,
    ],
  );

  useEffect(
    () => {
      if (!includeTestTemplates && !!isCheckedTestTemplates) {
        handleChange("roomTemplateId")(undefined);
        handleChange("isLocalRecEvent")(false);
        setIsCheckedTestTemplates(false);
      }
    },
    [includeTestTemplates],
  );

  const handleChangeCheckboxTestEvent = () => {
    handleChange("visible")(false);
    handleChange("billable")(false);
  };

  useEffect(
    () => {
      if (!!values.isInternalTest) {
        handleChangeCheckboxTestEvent();
      }
    },
    [values.isInternalTest],
  );

  const handleLocalRecModalOpen = value => {
    setLocalRecModalOpen(value);
  };

  return (
    <>
      <PageHeaderBar subtitle title="app.page.event.form.basicData" />
      <PageSection>
        <DataGrid>
          <DataGridItem>
            <FormTextField
              type="text"
              id="title"
              name="title"
              value={values.title}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.title" })}
            />
          </DataGridItem>
          {canEditEventType && (
            <DataGridItem>
              <EventTypeSelect
                isInsideForm
                label={intl.formatMessage({ id: "event.typeId" })}
                onChange={handleChange}
                contractId={values.contractId}
                value={values.typeId}
              />
            </DataGridItem>
          )}
          <DataGridItem>
            <FormChipInput
              id="keywords"
              name="keywords"
              label={intl.formatMessage({ id: "event.keywords" })}
              onChange={handleChange}
              defaultValue={values.keywords}
              helperText={intl.formatMessage({ id: "field.multipleSelect.help" })}
            />
          </DataGridItem>
          <DataGridItem big>
            <FormTextField
              type="text"
              id="description"
              name="description"
              value={values.description}
              onChange={handleChange}
              label={intl.formatMessage({ id: "common.description" })}
            />
          </DataGridItem>
          {showLocalRecEventFields && isAuthorizedRecording && (
            <DataGridItem>
              <FormCheckbox
                id="authorizedRecording"
                name="authorizedRecording"
                value="authorizedRecording"
                checked={values.authorizedRecording}
                onChange={handleChange}
                label={intl.formatMessage({ id: "event.authorizedRecording" })}
              />
            </DataGridItem>
          )}
          <DataGrid>
            {canEditStart && (
              <>
                <DataGridItem>
                  <FormDatePicker
                    className={classes.fullWidth}
                    value={values.startDate}
                    label={intl.formatMessage({ id: "common.since" })}
                    id="startDate"
                    name="startDate"
                    onChange={handleStartDateChange({
                      handleChange,
                      endDateName: "endDate",
                      endDateValue: values.endDate,
                    })}
                  />
                </DataGridItem>
              </>
            )}
            {canEditEnd && (
              <>
                <DataGridItem>
                  <FormDatePicker
                    className={classes.fullWidth}
                    value={values.endDate}
                    label={intl.formatMessage({ id: "common.until" })}
                    id="endDate"
                    name="endDate"
                    onChange={handleChange}
                  />
                </DataGridItem>
              </>
            )}
            <DataGridItem>
              <GraphicSetSelectByContract
                marginDense
                isInsideForm
                emptyValueText={intl.formatMessage({ id: "common.none" })}
                label={intl.formatMessage({ id: "event.graphicSet" })}
                onChange={handleChange}
                contractId={values.contractId}
                value={values.graphicSetId}
              />
            </DataGridItem>
            {(isNewForm || canEditRoomTemplate) && (
              <DataGridItem>
                <TemplateSelect
                  label={intl.formatMessage({ id: "event.template" })}
                  handleChange={handleChangeRoomTemplateId}
                  value={values.roomTemplateId}
                  contractId={values.contractId}
                  includeTestTemplates={includeTestTemplates}
                />
                <RestrictedToAdmin>
                  <FormCheckbox
                    id="includeTestTemplates"
                    name="includeTestTemplates"
                    value="includeTestTemplates"
                    checked={includeTestTemplates}
                    onChange={handleChangeIncludeTestTemplates}
                    label={intl.formatMessage({ id: "app.page.event.edit.searchTestTemplates" })}
                  />
                </RestrictedToAdmin>
              </DataGridItem>
            )}
            <RestrictedToAdmin>
              <DataGridItem>
                <FormCheckbox
                    id="isInternalTest"
                    name="isInternalTest"
                    value="isInternalTest"
                    checked={values.isInternalTest}
                    onChange={handleChange}
                    label={intl.formatMessage({ id: "event.isInternalTest" })}
                />
              </DataGridItem>
              <DataGridItem>
                <FormCheckbox
                  id="isMutedOnStart"
                  name="isMutedOnStart"
                  value="isMutedOnStart"
                  checked={values.isMutedOnStart}
                  onChange={handleChange}
                  label={
                    <div className={classes.mutedOnStartLabelContainer}>
                      <span>{intl.formatMessage({ id: "event.mutedOnStart" })}</span>
                      <Tooltip
                        title={
                          <span className={classes.mutedOnStartTooltipText}>
                            {intl.formatMessage(
                              { id: "event.mutedOnStart.tooltip" },
                              { minutes: Number(values.unmuteMinutesBefore ?? 0) },
                            )}
                          </span>
                        }
                        arrow
                      >
                        <div className={classes.mutedOnStartIconContainer}>
                          <Icon name="info" className={classes.mutedOnStartIcon} />
                        </div>
                      </Tooltip>
                    </div>
                  }
                />
              </DataGridItem>
              {values.isMutedOnStart && (
                <DataGridItem>
                  <FormTextField
                    type="number"
                    id="unmuteMinutesBefore"
                    name="unmuteMinutesBefore"
                    value={values.unmuteMinutesBefore}
                    onChange={handleChange}
                    label={intl.formatMessage(
                      { id: "event.unmuteMinutesBefore" },
                      { minutes: Number(values.unmuteMinutesBefore ?? 0) },
                    )}
                    inputProps={{ min: 0, max: 20, step: 1 }}
                  />
                </DataGridItem>
              )}
              <DataGridItem>
                <FormCheckbox
                    id="visible"
                    name="visible"
                    value="visible"
                    checked={values.visible}
                    onChange={handleChange}
                    label={intl.formatMessage({ id: "event.visible" })}
                    disabled={values.isInternalTest}
                />
              </DataGridItem>
              <DataGridItem>
                <FormCheckbox
                    id="billable"
                    name="billable"
                    value="billable"
                    checked={values.billable}
                    onChange={handleChange}
                    label={intl.formatMessage({ id: "event.billable" })}
                    disabled={values.isInternalTest}
                />
              </DataGridItem>
              {customer?.contract?.localRecAvailable && (
                  <DataGridItem>
                    <div className={classes.localRecContainer}>
                      <FormCheckbox
                          id="localRec"
                          name="localRec"
                          value="localRec"
                          checked={values.localRec}
                          onChange={name => checked => {
                            handleChange(name)(checked);
                            if (checked) handleLocalRecModalOpen(true);
                          }}
                          label={intl.formatMessage({ id: "event.local.rec" })}
                      />
                      <Tooltip title={intl.formatMessage({ id: "recording.localRecInfo" })} arrow>
                        <div className={classes.localRecIconContainer}>
                          <Icon name="info" className={classes.localRecIcon} />
                        </div>
                      </Tooltip>
                    </div>
                  </DataGridItem>
              )}
              {customer?.contract?.allowQod && (
                  <DataGridItem>
                    <div className={classes.localRecContainer}>
                      <FormCheckbox
                          id="activeQod"
                          name="activeQod"
                          value="activeQod"
                          checked={values.activeQod}
                          onChange={handleChange}
                          label={intl.formatMessage({ id: "event.activeQod" })}
                      />
                      <Tooltip
                        title={intl.formatMessage(
                          { id: "event.activeQod.msisdn" },
                          { msisdn: customer.contract.msisdnQod },
                        )}
                        arrow
                      >
                        <div className={classes.localRecIconContainer}>
                          <Icon name="info" className={classes.localRecIcon} />
                        </div>
                      </Tooltip>
                    </div>
                  </DataGridItem>
              )}
            </RestrictedToAdmin>
            {(canEditStart || canEditEnd) && (
              <DataGridItem big>
                <DurationHumanizer start={values.startDate} end={values.endDate} />
              </DataGridItem>
            )}
            {errorTextMixerSlots && (
              <DataGridItem big>
                <div className={classes.form__serverError} dangerouslySetInnerHTML={{ __html: errorTextMixerSlots }} />
              </DataGridItem>
            )}
          </DataGrid>
        </DataGrid>
      </PageSection>
      <ActionWithModal
        open={localRecModalOpen}
        setOpen={setLocalRecModalOpen}
        dialogTitle={intl.formatMessage({ id: "recording.alert.title" })}
        renderDialogContent={() => intl.formatMessage({ id: "recording.alert.text" })}
        showModalAccept={true}
        onAccept={() => handleLocalRecModalOpen(false)}
        showModalClose={false}
      />
    </>
  );
};

BasicForm.propTypes = {
  /**
   * Indica si se puede editar la plantilla
   */
  canEditRoomTemplate: PropTypes.bool,
  /**
   * Indica si se puede editar el tipo de evento
   */
  canEditEventType: PropTypes.bool,
  /**
   * Indica si el formulario se está utilizando para crear un evento
   */
  isNewForm: PropTypes.bool,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Callback que se dispara con el cambio del input
   */
  handleChange: PropTypes.func,
  /**
   * Mensaje de error para los slots de mixer
   */
  errorTextMixerSlots: PropTypes.string,
  /**
   * Indica si se muestran las plantillas de test
   */
  showTestTemplates: PropTypes.bool,
};

export default injectIntl(withStyles(styles)(connect(BasicForm)));
